import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { XYChartDisplay } from '../../../models/runnable_report';

import { computed, effect, ElementRef, inject, viewChild } from '@angular/core';
import { Chart, type ChartConfiguration, type ChartDataset, type ChartOptions, registerables } from 'chart.js';
import { ReportRunState } from '../report-run-state';
import { generateChartPalette } from '../chart-colors2';

@Component({
    selector: 'risr-features-reports-report-xy-chart',
    templateUrl: './report-xy-chart.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        .chart-container {
            display: block;
            width: 100%;
            overflow-x: auto;
            background-color: white;
        }`,
})
export class ReportXYChart {
    reportDisplayItem = input.required<XYChartDisplay>();

    private readonly reportRunState = inject(ReportRunState);

    protected readonly chartCanvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');

    private chart?: Chart<'scatter', object[], string>;

    private readonly reportResult = computed(() => {
        const name = this.reportDisplayItem().output;
        return this.reportRunState.getOutputResult(name)();
    });

    constructor() {
        Chart.register(...registerables);

        effect(() => {
            const canvasRef = this.chartCanvas();
            const result = this.reportResult();
            const display = this.reportDisplayItem();

            if (!canvasRef?.nativeElement) {
                return;
            }
            if (!result) {
                this.destroyChart();
                return;
            }

            const config = this.buildChartConfig(display, result.headers, result.data);
            if (!config) {
                this.destroyChart();
                return;
            }

            if (!this.chart) {
                this.destroyChart();
                this.chart = new Chart<'scatter', object[], string>(canvasRef.nativeElement, config);
                return;
            }

            this.chart.data = config.data;
            this.chart.options = config.options ?? {};
            this.chart.update();
        });
    }

    ngOnDestroy() {
        this.destroyChart();
    }

    private destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = undefined;
        }
    }

    private buildChartConfig(
        display: XYChartDisplay,
        headers: { id: string; type: string }[],
        rows: unknown[][]
    ): ChartConfiguration<'scatter', object[], string> | undefined {
        const points = display.points || [];
        if (points.length === 0) {
            return undefined;
        }

        const headerIndex = headers.reduce((acc, h, index) => {
            acc.set(h.id, index);
            return acc;
        }, new Map<string, number>());

        const datasets = points
            .map((p): ChartDataset<'scatter', object[]> | undefined => {
                const xIdx = headerIndex.get(p.xColumn);
                const yIdx = headerIndex.get(p.yColumn);
                if (xIdx === undefined || yIdx === undefined) {
                    return undefined;
                }

                const data = rows
                    .map((row) => {
                        const x = this.toNumber(row?.[xIdx]);
                        const y = this.toNumber(row?.[yIdx]);
                        return { x, y };
                    })
                    .filter((pt) => Number.isFinite((pt as any).x) && Number.isFinite((pt as any).y));

                const colors = generateChartPalette({
                    count: data.length,
                    seedHex: p.color || '#f29f05' // The default color is advance
                });

                return {
                    label: p.label || `${p.xColumn} vs ${p.yColumn}`,
                    data,
                    backgroundColor: colors,
                    borderWidth: 1,
                    pointRadius: 3,
                };
            })
            .filter((d): d is ChartDataset<'scatter', object[]> => d !== undefined);

        const xMinType = display.options?.xMinType || 'auto';
        const xMaxType = display.options?.xMaxType || 'auto';
        const xMin = display.options?.xMin;
        const xMax = display.options?.xMax;

        const yMinType = display.options?.yMinType || 'auto';
        const yMaxType = display.options?.yMaxType || 'auto';
        const yMin = display.options?.yMin;
        const yMax = display.options?.yMax;


        const options: ChartOptions<'scatter'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: datasets.length > 1,
                },
                title: {
                    display: !!display.title,
                    text: display.title || '',
                },
            },
            scales: {
                x: {
                    title: {
                      display: !!display.horizontalLabel,
                      text: display.horizontalLabel
                    },
                    type: 'linear',
                    position: 'bottom',
                    min: xMinType === 'fixed' ? xMin : undefined,
                    max: xMaxType === 'fixed' ? xMax : undefined,
                },
                y: {
                    title: {
                        display: !!display.verticalLabel,
                        text: display.verticalLabel
                    },
                    type: 'linear',
                    min: yMinType === 'fixed' ? yMin : undefined,
                    max: yMaxType === 'fixed' ? yMax : undefined,
                },
            },
        };

        return {
            type: 'scatter',
            data: {
                datasets,
            },
            options,
        };
    }

    private toNumber(value: unknown): number {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const n = Number(value);
            return Number.isFinite(n) ? n : Number.NaN;
        }
        return Number.NaN;
    }
}
